import {processRegisteredPerfMark} from './processRegisteredPerfMark';
import {validateDurationContracts} from './validateDurationContracts';

type PerfObserverState = {
  observer: PerformanceObserver | null;
  started: boolean;
};

export const perfObserverState: PerfObserverState = {
  observer: null,
  started: false,
};

/**
 * Called from consentAwareAnalyticSetup during analytics setup
 * Bridges local marks to telemetry, including marks recorded before start
 * (buffered:true), so startup timings are not lost
 * Uses one shared observer so setup can run more than once safely
 */
export function startPerfObserver(): PerformanceObserver | null {
  if (perfObserverState.started) {
    return perfObserverState.observer;
  }

  // Note - remove/expand this check once we have one consolidated performance observer and we support more entryTypes
  if (!canObserveMarkEntries()) {
    return null;
  }

  validateDurationContracts();

  const observer = new PerformanceObserver(list => {
    for (const entry of list.getEntriesByType('mark')) {
      processRegisteredPerfMark(entry as PerformanceMark);
    }
  });

  if (!observeMarkEntries(observer)) {
    observer.disconnect();
    return null;
  }

  perfObserverState.observer = observer;
  perfObserverState.started = true;
  return perfObserverState.observer;
}

function canObserveMarkEntries() {
  if (typeof PerformanceObserver !== 'function') {
    return false;
  }

  const supportedEntryTypes = PerformanceObserver.supportedEntryTypes;
  return (
    Array.isArray(supportedEntryTypes) && supportedEntryTypes.includes('mark')
  );
}

function observeMarkEntries(observer: PerformanceObserver) {
  try {
    // Buffered marks let startup catch marks that already happened
    // https://developer.mozilla.org/en-US/docs/Web/API/PerformanceObserver/observe
    // https://www.w3.org/TR/performance-timeline/#dom-performanceobserver-observe
    observer.observe({type: 'mark', buffered: true});
    return true;
  } catch {
    return false;
  }
}
